import { useId, useRef, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import {
  ConfirmDialog as CommonConfirmDialog,
  type ConfirmDialogProps as CommonConfirmDialogProps,
} from '@/admin-design/_common/components/ConfirmDialog'
import { cn } from '@/lib/utils'
import { IconButton } from './Button'
import { useOverlay } from './overlay'

export interface DialogProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  /** The action row; the first button in it should be the cancel. */
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}

const WIDTH = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-xl' }

/** A centred modal for one small task: a reason to reject, a URL to record.
 *  Destructive yes/no questions use ConfirmDialog below. */
export function Dialog({ open, onClose, title, description, children, footer, size = 'md' }: DialogProps) {
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descId = useId()
  useOverlay(open, onClose, panel)
  if (!open) return null
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cn(
          'relative w-full rounded-t-xl border border-border bg-card text-card-foreground shadow-overlay outline-none sm:rounded-xl',
          WIDTH[size],
        )}
      >
        <div className="flex items-start gap-3 px-5 pb-2 pt-4">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-section font-semibold">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-1 text-body text-muted-foreground">
                {description}
              </p>
            )}
          </div>
          <IconButton label="닫기" icon={X} size="sm" onClick={onClose} className="-mr-2 -mt-1" />
        </div>
        {children && <div className="px-5 py-2">{children}</div>}
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 px-5 pb-4 pt-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  )
}

export type ConfirmDialogProps = CommonConfirmDialogProps

const DANGER_FILL = { '--destructive': 'var(--danger)', '--destructive-foreground': 'var(--danger-foreground)' } as CSSProperties

/** Yes/no before a destructive or irreversible action — delete, unschedule,
 *  publish now, end a giveaway, hand out public codes. The engine is
 *  _common's; this only sets Korean labels. */
export function ConfirmDialog({ confirmLabel = '확인', cancelLabel = '취소', ...rest }: ConfirmDialogProps) {
  // The shared dialog fills its destructive button with `--destructive`,
  // which in dark mode is the light red used for text. Inside this box it
  // takes the console's solid danger red instead, like every other danger
  // button.
  return (
    <div className="contents" style={DANGER_FILL}>
      <CommonConfirmDialog confirmLabel={confirmLabel} cancelLabel={cancelLabel} {...rest} />
    </div>
  )
}
